import React, { useRef, useEffect, useState } from 'react';
import { lockBodyScroll } from '../utils/bodyScrollLock';

export default function BottomSheet({ isOpen, onClose, children, variant, className = '' }) {
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const [translateY, setTranslateY] = useState(0);
  const [closing, setClosing] = useState(false);
  const isProfile = variant === 'profile';

  useEffect(() => {
    if (isOpen) {
      setTranslateY(0);
      setClosing(false);
      return lockBodyScroll();
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const el = sheetRef.current;
    if (!el) return;

    function onTouchStart(e) {
      const handle = e.target.closest('.vi-bottom-sheet__handle');
      if (!handle && isProfile) return;
      startY.current = e.touches[0].clientY;
      currentY.current = 0;
    }

    function onTouchMove(e) {
      const dy = e.touches[0].clientY - startY.current;
      if (dy < 0) return;
      currentY.current = dy;
      setTranslateY(dy);
      if (dy > 60 && e.cancelable) e.preventDefault();
    }

    function onTouchEnd() {
      if (currentY.current > 120) {
        setClosing(true);
        setTimeout(() => { onClose(); setTranslateY(0); setClosing(false); }, 200);
      } else {
        setTranslateY(0);
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isOpen, onClose, isProfile]);

  if (!isOpen) return null;

  const sheetClass = [
    'vi-bottom-sheet',
    isProfile ? 'vi-bottom-sheet--profile' : '',
    closing ? 'vi-bottom-sheet--closing' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`vi-bottom-sheet-overlay${closing ? ' vi-bottom-sheet-overlay--closing' : ''}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={sheetRef}
        className={sheetClass}
        onClick={(e) => e.stopPropagation()}
        style={{ transform: closing ? undefined : `translateY(${translateY}px)` }}
        role="dialog"
        aria-modal="true"
      >
        <div className="vi-bottom-sheet__handle" aria-hidden>
          <div className="vi-bottom-sheet__handle-bar" />
        </div>
        <div className="vi-bottom-sheet__body">
          {children}
        </div>
      </div>
    </div>
  );
}